import { MembershipPromoCode, NotificationType } from "@markit/common.types";
import React, { useCallback, useMemo, useState } from "react";
import { useTheme } from "../../../hooks/useTheme";
import ThreeDotsButton from "../../Buttons/ThreeDotsButton";
import { HorizontalDivider } from "../../Dividers/HorizontalDivider";
import { DropdownMenu, DropdownMenuItem } from "../../DropdownMenu";
import useAsyncOnMount from "../../../hooks/useAsyncEffectOnMount";
import { useDispatch, useSelector } from "react-redux";
import { getAccountState } from "../../../redux/slices/accountSlice";
import { getMembershipPromoCodeUsageCount } from "../../../utils/membershipUtils";
import ConfirmDeleteModal from "../../Containers/ConfirmPopups/ConfirmDeleteModal";
import { MembershipPromoCodeModal } from "./MembershipPromoCodeModal";
import { showNotificationBanner } from "../../../utils/notificationUtils";
import AlertContainer from "../../Containers/AlertContainer";
import { API } from "../../../API";

export const MEMBERSHIP_PROMO_FLEX_SECTIONS = [7, 4, 4, 2];

type MembershipPromoCodeItemProps = {
  item: MembershipPromoCode;
};

export const MembershipPromoCodeItem = (
  props: MembershipPromoCodeItemProps
) => {
  const { item } = props;
  const { theme } = useTheme();
  const dispatch = useDispatch();
  const { account } = useSelector(getAccountState);
  const { accountData } = account;

  const [alertText, setAlertText] = useState({ heading: "", subHeading: "" });
  const [isPromoModalVisible, setIsPromoModalVisible] = useState(false);
  const [deletePromoConfirm, setDeletePromoConfirm] = useState(false);
  const [promoCodeUses, setPromoCodeUses] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  useAsyncOnMount(async () => {
    setIsLoading(true);
    try {
      const count = await getMembershipPromoCodeUsageCount(
        accountData.uid,
        item.id
      );
      setPromoCodeUses(count);
    } catch (error) {
      console.error("Error fetching promo code usage:", error);
    }
    setIsLoading(false);
  });

  const copyPromoLink = useCallback(async () => {
    navigator.clipboard
      .writeText(item.alias)
      .then(() => {
        showNotificationBanner(
          dispatch,
          "Copied!",
          NotificationType.AFFIRMATIVE
        );
      })
      .catch((err) => {
        setAlertText({ heading: err, subHeading: "" });
      });
  }, [dispatch, item.alias]);

  const editPromo = useCallback(() => {
    setIsPromoModalVisible(true);
  }, []);

  const deletePromoCode = useCallback(async () => {
    await API.user
      .deactivateMembershipPromoCode({
        uid: accountData.uid,
        membershipPromoCodeId: item.id,
      })
      .then((response) => {
        if (!response.success) {
          setAlertText({
            heading: "Error deleting promo code.",
            subHeading: "Please try again.",
          });
        } else {
          showNotificationBanner(
            dispatch,
            "Promo Code Deactivated",
            NotificationType.NEGATIVE,
            "ion:trash"
          );
        }
      })
      .catch(() => {
        setAlertText({
          heading: "Error deactivating promo code.",
          subHeading: "Please try again.",
        });
      });
  }, [accountData.uid, dispatch, item.id]);

  const dropdownItems: DropdownMenuItem[] = useMemo(
    () => [
      {
        title: "Copy Name",
        icon: "feather:link-2",
        key: "copy_link",
        onPress: () => copyPromoLink(),
      },
      {
        title: "Edit Promo",
        icon: "ion:create",
        key: "edit_promo",
        onPress: editPromo,
      },
      {
        title: "Deactivate Promo",
        icon: "ion:trash",
        key: "delete_promo",
        onPress: () => setDeletePromoConfirm(true),
        isDestructive: true,
      },
    ],
    [copyPromoLink, editPromo]
  );

  return (
    <div key={item.id}>
      <div
        style={{
          display: "flex",
          paddingInline: 14,
          paddingBlock: 7,
          gap: 14,
        }}
      >
        <span
          style={{
            flex: MEMBERSHIP_PROMO_FLEX_SECTIONS[0],
          }}
          className="bodyBold"
        >
          {item.alias}
        </span>
        <div style={{ flex: MEMBERSHIP_PROMO_FLEX_SECTIONS[1] }}>
          <span
            style={{
              marginLeft: 16,
            }}
            className="bodySubtext"
          >
            {`${item.value}%`}
          </span>
        </div>
        <div style={{ flex: MEMBERSHIP_PROMO_FLEX_SECTIONS[2] }}>
          <span
            style={{
              marginLeft: 18,
            }}
            className="bodySubtext"
          >
            {item.durationInBillingCycles === 0
              ? "Lifetime"
              : `${item.durationInBillingCycles} Months`}
          </span>
        </div>
        <div
          className="AlignedRowSpaced"
          style={{ flex: MEMBERSHIP_PROMO_FLEX_SECTIONS[3] }}
        >
          <span
            style={{
              paddingLeft: 20,
            }}
            className="bodySubtext"
          >
            {isLoading
              ? "--"
              : item.maxRedemptions === 0
              ? `${promoCodeUses}`
              : `${promoCodeUses}/${item.maxRedemptions}`}
          </span>
          {item.active ? (
            <div>
              <DropdownMenu
                dropdownItems={dropdownItems}
                trigger={["click"]}
                placement="bottomLeft"
                altWidth={150}
              >
                <ThreeDotsButton
                  onPress={() => {
                    console.log(item.id);
                  }}
                />
              </DropdownMenu>
            </div>
          ) : null}
        </div>
      </div>
      <HorizontalDivider altMargin={14} />
      {isPromoModalVisible ? (
        <MembershipPromoCodeModal
          accountData={accountData}
          setIsPromoModalVisible={setIsPromoModalVisible}
          existingPromo={item}
        />
      ) : null}
      <AlertContainer
        headerComp={alertText.heading}
        subHeaderComp={
          alertText.subHeading !== "" ? alertText.subHeading : undefined
        }
        theme={theme}
        closeModal={() => setAlertText({ heading: "", subHeading: "" })}
        hideModal={alertText.heading === "" && alertText.subHeading === ""}
      />
      <ConfirmDeleteModal
        heading="Deactivate Promo Code?"
        subtext={
          "Deactivating this coupon will not affect discounts for members who have already redeemed the coupon, but new redemptions of the coupon won't be allowed.\n\nAre you sure you want to delete this promo code? This can't be undone."
        }
        deleteButtonText="Deactivate"
        hideModal={!deletePromoConfirm}
        setIsVisible={setDeletePromoConfirm}
        deleteOnPress={deletePromoCode}
        theme={theme}
      />
    </div>
  );
};
